/**
 * The card on file for membership instalments and the annual maintenance fee.
 * Replacing it uses a Stripe SetupIntent — no charge, just a card stored for later.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { toast } from 'sonner';
import { CreditCard, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import { stripePromise } from '@/lib/stripe';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CardForm = ({ onSaved, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError('');
    const { error: stripeError, setupIntent } = await stripe.confirmSetup({
      elements,
      // Stripe requires a return_url at confirmation even when nothing redirects.
      confirmParams: { return_url: `${window.location.origin}/dashboard/membership` },
      redirect: 'if_required',
    });
    if (stripeError) {
      setError(stripeError.message || 'We could not save that card');
      setSaving(false);
      return;
    }
    try {
      const res = await axios.post(`${API}/membership/card`,
        { payment_method_id: setupIntent.payment_method }, auth());
      toast.success('Your card is updated');
      onSaved?.(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'We could not save that card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="membership-card-form">
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && (
        <p style={{ color: '#a02828', fontSize: '.85rem', marginTop: 10 }} data-testid="membership-card-error">
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        <Button type="submit" disabled={saving} data-testid="membership-card-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
          Save this card
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
      <p style={{ fontSize: '.8rem', color: '#8a8a86', marginTop: 12 }}>
        Nothing is charged now. We only store the card for your scheduled payments.
      </p>
    </form>
  );
};

export const MembershipCard = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState('');
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/membership/card`, auth());
      setData(res.data);
    } catch (error) {
      setData({ card: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/membership/card/setup-intent`, {}, auth());
      setClientSecret(res.data.client_secret);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start the card update');
    } finally {
      setStarting(false);
    }
  };

  const finished = (summary) => {
    setClientSecret('');
    setData(summary);
    onChanged?.();
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin" />;

  const card = data?.card;
  const trouble = card && (card.expired || card.expires_before_next_payment);

  return (
    <div data-testid="membership-card-section">
      {card ? (
        <>
          <div className="mm-row" style={{ borderBottom: 0, paddingTop: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {trouble
                ? <AlertTriangle className="w-4 h-4" style={{ color: '#a02828' }} />
                : <CheckCircle2 className="w-4 h-4" style={{ color: '#2a6a3a' }} />}
              <span><span style={{ textTransform: 'capitalize' }}>{card.brand}</span> ending {card.last4}</span>
            </span>
            <span style={{ color: trouble ? '#a02828' : 'var(--ink-light)' }} data-testid="membership-card-expiry">
              {card.expired ? 'expired' : 'expires'} {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
            </span>
          </div>
          {trouble && (
            <p className="mm-note" style={{ color: '#a02828' }} data-testid="membership-card-warning">
              This card {card.expired ? 'has expired' : 'expires before your next payment'} — please replace
              it so nothing gets declined.
            </p>
          )}
        </>
      ) : (
        <p className="mm-note" data-testid="membership-card-none">
          No card is saved for your membership payments yet.
        </p>
      )}

      {!clientSecret ? (
        <div className="mm-actions">
          <Button variant={trouble ? 'default' : 'outline'} onClick={start} disabled={starting}
                  data-testid="membership-card-replace-btn">
            {starting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {card ? 'Replace this card' : 'Add a card'}
          </Button>
        </div>
      ) : (
        <div style={{ marginTop: 18 }}>
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
            <CardForm onSaved={finished} onCancel={() => setClientSecret('')} />
          </Elements>
        </div>
      )}
    </div>
  );
};

export default MembershipCard;
